import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Info, Percent, Euro, Plus, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * FA-managed "Payment Processing Charges" — single-mode base rate + optional
 * per-audience overrides (unregistered / guest / member), same audience
 * taxonomy the ticket audience selector uses.
 *
 * Wire model matches backend:
 *   { type: "percent"|"fixed", value: N, overrides: { <audience>: {type,value} } }
 */
const AUDIENCE_ROWS = [
  { id: "unregistered", label: "Unregistered", hint: "OTP guests and unregistered online buyers" },
  { id: "guest", label: "Guest", hint: "Registered guest accounts" },
  { id: "member", label: "Member", hint: "Registered members (any type)" },
];

function ChargeRuleInputs({ rule, onChange, testIdPrefix }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
      <div className="flex gap-1">
        {[
          { id: "percent", icon: Percent },
          { id: "fixed", icon: Euro },
        ].map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onChange({ ...rule, type: m.id })}
            className={`p-2 rounded-lg border transition-colors ${rule.type === m.id ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid={`${testIdPrefix}-mode-${m.id}`}
            title={m.id === "percent" ? "Percentage" : "Fixed euro"}
          >
            <m.icon className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">
          {rule.type === "percent" ? "%" : "\u20ac"}
        </span>
        <input
          type="number" step="0.01" min="0"
          max={rule.type === "percent" ? "100" : undefined}
          value={rule.value}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          className="w-full pl-7 pr-2 py-2 rounded-lg border font-mono text-sm"
          data-testid={`${testIdPrefix}-value`}
        />
      </div>
    </div>
  );
}

export default function DigitalChargesSettings() {
  const [base, setBase] = useState({ type: "percent", value: "" });
  const [overrides, setOverrides] = useState({}); // { audience_id: {type, value} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const res = await authAxios.get("/finance-admin/settings/payment-charges");
        setBase({ type: res.data.type || "percent", value: String(res.data.value ?? "") });
        // Normalize to strings for controlled inputs.
        const rawOverrides = res.data.overrides || {};
        const norm = {};
        Object.keys(rawOverrides).forEach((k) => {
          norm[k] = { type: rawOverrides[k].type, value: String(rawOverrides[k].value ?? "") };
        });
        setOverrides(norm);
      } catch (e) {
        toast.error("Failed to load charges");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const validateRule = (rule, label) => {
    const v = parseFloat(rule.value);
    if (isNaN(v) || v < 0) { toast.error(`${label} value must be \u2265 0`); return null; }
    if (rule.type === "percent" && v > 100) { toast.error(`${label} percent must be 0\u2013100`); return null; }
    return { type: rule.type, value: v };
  };

  const handleSave = async () => {
    const baseRule = validateRule(base, "Base");
    if (!baseRule) return;
    const payloadOverrides = {};
    for (const [key, rule] of Object.entries(overrides)) {
      const clean = validateRule(rule, key);
      if (!clean) return;
      payloadOverrides[key] = clean;
    }
    setSaving(true);
    try {
      await authAxios.put("/finance-admin/settings/payment-charges", {
        ...baseRule,
        overrides: payloadOverrides,
      });
      toast.success("Payment processing charges saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addOverride = (audienceId) => {
    // Seed the override with a copy of the base rule so the FA can tweak from there.
    setOverrides((o) => ({ ...o, [audienceId]: { type: base.type, value: base.value } }));
  };
  const removeOverride = (audienceId) => {
    setOverrides((o) => { const next = { ...o }; delete next[audienceId]; return next; });
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  const previewNet = 25;
  const baseV = parseFloat(base.value) || 0;
  const previewCharge = base.type === "percent" ? (previewNet * baseV) / 100 : baseV;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm max-w-xl" data-testid="payment-charges-settings">
      <h2 className="font-bold text-lg text-[#1e3a5f] mb-2">Payment Processing Charges</h2>
      <p className="text-sm text-slate-500 mb-5">
        Applied to online (Stripe) payments. Set a base rate below, and optionally override it
        for specific audiences. Card sales at the accountant desk use the POS charges below.
      </p>

      {/* Base charge */}
      <div className="border rounded-xl p-4 mb-5">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">Base Rate</p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {[
            { id: "percent", label: "Percentage", icon: Percent },
            { id: "fixed", label: "Fixed", icon: Euro },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setBase({ ...base, type: opt.id })}
              className={`p-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors ${base.type === opt.id ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              data-testid={`payment-charges-mode-${opt.id}`}
            >
              <opt.icon className="w-4 h-4" />
              {opt.label}
            </button>
          ))}
        </div>
        <label className="text-xs text-slate-500 mb-1 block">
          {base.type === "percent" ? "Rate (%)" : "Amount (\u20ac)"}
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">
            {base.type === "percent" ? "%" : "\u20ac"}
          </span>
          <input
            type="number" step="0.01" min="0"
            max={base.type === "percent" ? "100" : undefined}
            value={base.value}
            onChange={(e) => setBase({ ...base, value: e.target.value })}
            className="w-full p-3 pl-9 rounded-xl border font-mono text-lg"
            data-testid="payment-charges-value-input"
          />
        </div>
      </div>

      {/* Per-audience overrides */}
      <div className="border rounded-xl p-4 mb-5">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">Per-Audience Overrides</p>
        <p className="text-xs text-slate-500 mb-3">Any audience without an override falls back to the base rate.</p>
        <div className="space-y-3">
          {AUDIENCE_ROWS.map((row) => {
            const active = overrides[row.id];
            return (
              <div key={row.id} className="border-b last:border-0 pb-3 last:pb-0">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-bold text-[#1e3a5f]">{row.label}</p>
                    <p className="text-[11px] text-slate-400">{row.hint}</p>
                  </div>
                  {active ? (
                    <button
                      onClick={() => removeOverride(row.id)}
                      className="text-red-500 hover:bg-red-50 rounded-full p-1"
                      data-testid={`payment-override-${row.id}-remove`}
                      title="Remove override (use base rate)"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => addOverride(row.id)}
                      className="text-[#1e3a5f] hover:bg-slate-50 rounded-full px-2 py-1 text-xs font-bold flex items-center gap-1"
                      data-testid={`payment-override-${row.id}-add`}
                    >
                      <Plus className="w-3 h-3" /> Add override
                    </button>
                  )}
                </div>
                {active && (
                  <ChargeRuleInputs
                    rule={active}
                    onChange={(next) => setOverrides((o) => ({ ...o, [row.id]: next }))}
                    testIdPrefix={`payment-override-${row.id}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 flex items-start gap-2">
        <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-600">
          Base preview: on a &euro;{previewNet.toFixed(2)} transaction the base rate adds
          <strong className="text-[#1e3a5f]"> &euro;{previewCharge.toFixed(2)}</strong>
          {" "}({base.type === "percent" ? `${baseV || 0}%` : "flat"}) {"\u2014"} buyer pays &euro;{(previewNet + previewCharge).toFixed(2)}.
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="px-6 py-3 rounded-full bg-[#1e3a5f] text-white font-bold flex items-center gap-2 disabled:opacity-50"
        data-testid="payment-charges-save-btn"
      >
        <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Charges"}
      </button>
    </div>
  );
}
